import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FaCheck, FaClipboardCheck, FaShieldAlt, FaTasks, FaUserTag, FaBuilding, FaTimes } from "react-icons/fa";
import { FiSearch } from "react-icons/fi";
import Handler from "../../Helpers/Handler";
import { textCapitalize } from "../../Helpers/Utils";

// Task types a role can be given: the delivery / pickup work its staff can be assigned.
const ROLE_TASK_TYPES = ["delivery", "pickup"];

const RoleFormModal = ({ mode = "create", role, departments = [], onClose, onSaved }) => {
    const modalRef = useRef(null);
    const bsModalRef = useRef(null);
    const [roleName, setRoleName] = useState(role?.roleName || "");
    const [departmentId, setDepartmentId] = useState(role?.departmentId ||departments?.[0]?.departmentId ||"");
    const [permissions, setPermissions] = useState(role?.permissions || []);
    const [tasks, setTasks] = useState(role?.tasks || []);
    const [shiftType, setShiftType] = useState(role?.shiftType || "");
    const [shiftTypes, setShiftTypes] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [permissionSearch, setPermissionSearch] = useState("");
    const [loadingCatalog, setLoadingCatalog] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) {
            return undefined;
        }
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        bsModalRef.current = instance;
        const handleHidden = () => { onClose?.(); };
        el.addEventListener("hidden.bs.modal",handleHidden);
        instance.show();
        return () => {
            el.removeEventListener("hidden.bs.modal",handleHidden);
            instance.hide();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const loadCatalog = async () => {
            setLoadingCatalog(true);
            const response = await Handler({ method: "GET", url: "/admin/staff/role/capabilities", });
            setCatalog( response?.success ? response?.data?.data || []: []);
            setLoadingCatalog(false);
        };
        loadCatalog();

        const loadShiftTypes = async () => {
            const response = await Handler({ method: "GET", url: "/admin/staff/role/shift-types" });
            setShiftTypes(response?.success ? response?.data?.data || [] : []);
        };
        loadShiftTypes();
    }, []);

    // Grouped by module, then narrowed by the search box. A module whose every
    // entry is filtered out drops away entirely rather than leaving an empty header.
    const catalogByModule = useMemo(() => {
        const term = permissionSearch.trim().toLowerCase();
        const groups = {};
        catalog.forEach((item) => {
            if (term
                && !item.label.toLowerCase().includes(term)
                && !item.key.toLowerCase().includes(term)
                && !item.module.toLowerCase().includes(term)) return;
            if (!groups[item.module]) {
                groups[item.module] = [];
            }
            groups[item.module].push(item);
        });
        return groups;
    }, [catalog, permissionSearch]);


    const selectedDepartment = departments.find( (department) => department.departmentId === departmentId);
    const availableTasks = ROLE_TASK_TYPES;


    const labelOf = useMemo(() => Object.fromEntries(catalog.map((item) => [item.key, item.label])), [catalog]);
    const itemOf = useMemo(() => Object.fromEntries(catalog.map((item) => [item.key, item])), [catalog]);

    // Ticking an action also ticks what it needs (always) and what it works best
    // with (recommended). Both stay visible and editable; unticking an action
    // never removes views, so a role keeps exactly what was ticked.
    const togglePermission = (key) => {
        setPermissions((previous) => {
            if (previous.includes(key)) return previous.filter((permission) => permission !== key);
            const item = itemOf[key];
            const extras = [...(item?.requires || []), ...(item?.recommends || [])];
            return [...new Set([...previous, key, ...extras])];
        });
    };

    const addMissing = (keys) => setPermissions((previous) => [...new Set([...previous, ...keys])]);

    // For a view that is not ticked: which ticked actions need it.
    const neededBy = (viewKey) => permissions
        .filter((key) => (itemOf[key]?.requires || []).includes(viewKey))
        .map((key) => labelOf[key] || key);

    const toggleModuleAll = ( moduleKeys, allSelected ) => {
        setPermissions((previous) => {
            if (allSelected) {
                return previous.filter( (permission) => !moduleKeys.includes(permission) );
            }
            return [...previous, ...moduleKeys].filter((value, index, all) => all.indexOf(value) === index);
        });
    };


    const getModuleSelectedCount = (items) => {
        return items.filter((item) =>permissions.includes(item.key)).length;
    };


    const toggleTask = (task) => {
        setTasks((previous) => previous.includes(task) ? previous.filter( (item) => item !== task ) : [...previous, task]);
    };

    const onDepartmentChange = ( nextDepartmentId ) => {
        setDepartmentId(nextDepartmentId);
    };


    const onSubmit = async () => {
        const name = roleName.trim();

        if (name.length < 2) {
            toast.error("Role name must be at least 2 characters");
            return;
        }

        if (!departmentId) {
            toast.error("Select a department");
            return;
        }

        if (!shiftType) {
            toast.error("Select a shift type");
            return;
        }

        if (!permissions.length) {
            toast.error("Select at least one permission");
            return;
        }

        setSaving(true);

        const payload = {
            roleName: name,
            departmentId,
            shiftType,
            permissions,
            tasks,
        };

        const response =
            mode === "edit"
                ? await Handler({
                      method: "patch",
                      url: `/admin/staff/role/${role.roleId}`,
                      data: payload,
                  })
                : await Handler({
                      method: "post",
                      url: "/admin/staff/role/register",
                      data: payload,
                  });

        if (response?.success) {
            toast.success( response?.data?.message || (mode === "edit" ? "Role updated" : "Role created"));
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to save the role"));
        }

        setSaving(false);
    };

    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true" >
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: "900px", }} >
                <div className="modal-content border-0 overflow-hidden" style={{ borderRadius: "20px", boxShadow: "0 20px 60px rgba(0,0,0,.18)", }} >
                    <div className="modal-header border-0 px-4 py-3 d-flex justify-content-between" style={{ background: "linear-gradient(180deg, #f8faff 0%, #ffffff 100%)", }} >
                        <div className="d-flex align-items-center gap-3">
                            <div className="d-flex align-items-center justify-content-center rounded-3 bg-primary bg-opacity-10 text-primary" style={{ width: 46, height: 46, }} >
                                <FaUserTag size={19} />
                            </div>
                            <div>
                                <h5 className="fw-semibold mb-1"> {mode === "edit" ? "Edit role" : "Create role"} </h5>
                                <div className="text-muted small">
                                    {mode === "edit" ? "Update role permissions and responsibilities" : "Create a role and define what it can access"}
                                </div>
                            </div>

                        </div>

                        <button type="button" className="btn btn-light rounded-circle d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, }} data-bs-dismiss="modal" aria-label="Close" >
                            <FaTimes size={13} />
                        </button>
                    </div>

                    <div className="modal-body px-4 py-4" style={{ background: "#f8f9fb", }} >

                        <section className="mb-4">
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <div className="d-flex align-items-center justify-content-center rounded-2 bg-primary bg-opacity-10 text-primary" style={{ width: 34, height: 34, }} >
                                    <FaBuilding size={14} />
                                </div>
                                <div>
                                    <h6 className="fw-semibold mb-0"> Role information </h6>
                                    <small className="text-muted"> Define the role's basic details </small>
                                </div>
                            </div>
                            <div className="bg-white border rounded-4 p-4">
                                <div className="row g-4">
                                    <div className="col-12 col-md-6">
                                        <label className="form-label small fw-semibold"> Role name </label>
                                        <input type="text" className="form-control form-control-lg" value={roleName} onChange={(e) =>setRoleName(e.target.value)} placeholder="e.g. Hub Supervisor" />
                                        <div className="form-text"> Use a clear name that describes the responsibility.</div>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <label className="form-label small fw-semibold"> Shift Type </label>
                                        <div className="position-relative">
                                            <select className="form-select form-select-lg pe-5" value={shiftType} onChange={(e) => setShiftType(e.target.value)} >
                                                <option value="" disabled> Select shift type </option>
                                                {shiftTypes.map((item) => (<option key={item.value} value={item.value}>{item.label}</option>))}
                                            </select>
                                        </div>
                                        <div className="form-text"> Working pattern this role is rostered on.</div>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <label className="form-label small fw-semibold"> Department </label>
                                        <div className="position-relative">
                                            <select className="form-select form-select-lg pe-5" value={departmentId} onChange={(e) =>onDepartmentChange(e.target.value ) } >
                                                <option value="" disabled > Select department </option>
                                                {departments.map( (department) => ( <option key={ department.departmentId } value={ department.departmentId } > { department.departmentName } </option> ))}
                                            </select>
                                        </div>
                                        {selectedDepartment && (
                                            <div className="mt-2 small text-muted"> { availableTasks.length }{" "} task type
                                                {availableTasks.length !== 1 ? "s" : ""}{" "} available
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="mb-4">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="d-flex align-items-center justify-content-center rounded-2 bg-primary bg-opacity-10 text-primary" style={{ width: 34, height: 34, }} >
                                        <FaShieldAlt size={14} />
                                    </div>
                                    <div>
                                        <h6 className="fw-semibold mb-0">  Permissions </h6>
                                        <small className="text-muted"> Control what this role can access </small>
                                    </div>
                                </div>
                                <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary px-3 py-2"> {permissions.length} selected </span>
                            </div>
                            <div className="role-search mb-2">
                                <FiSearch />
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search permissions by name, key or module..."
                                    value={permissionSearch}
                                    onChange={(e) => setPermissionSearch(e.target.value)}
                                />
                            </div>
                            <div className="bg-white border rounded-4 overflow-hidden">
                                {loadingCatalog ? (

                                    <div className="p-5 text-center">
                                        <div className="spinner-border spinner-border-sm text-primary mb-3" role="status" />
                                        <div className="small text-muted"> Loading permissions... </div>
                                    </div>
                                ) : Object.keys(catalogByModule).length ? (
                                    <div className="p-3">
                                        {Object.entries(catalogByModule).map(
                                            ( [ moduleName, items, ], moduleIndex ) => {
                                                const moduleKeys = items.map((item) => item.key );
                                                const selectedCount = getModuleSelectedCount(items);
                                                const allSelected = selectedCount === items.length;
                                                return (
                                                    <div key={ moduleName } className={ moduleIndex !== 0 ? "border-top pt-3 mt-3" : "" } >
                                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <span className="fw-semibold text-dark"> { moduleName }</span>
                                                                <span className="badge rounded-pill text-bg-light border"> { selectedCount } / { items.length }</span>
                                                            </div>
                                                            <button type="button" className={`btn btn-sm ${ allSelected ? "btn-light" : "btn-outline-primary" }`} onClick={() => toggleModuleAll( moduleKeys, allSelected )} >
                                                                {allSelected ? "Clear all" : "Select all"}
                                                            </button>
                                                        </div>
                                                        <div className="row g-2">
                                                            {items.map( ( item ) => {
                                                                    const selected = permissions.includes( item.key );
                                                                    return (
                                                                        <div key={ item.key } className="col-12 col-md-6" >
                                                                            <label className={`d-flex align-items-center gap-3 border rounded-3 p-3 h-100 ${ selected ? "border-primary bg-primary bg-opacity-10" : "bg-white" }`} style={{ cursor: "pointer", transition: "all .15s ease", }} >
                                                                                <div className={`d-flex align-items-center justify-content-center rounded-2 flex-shrink-0 ${ selected ? "bg-white text-primary" : " bg-light text-muted" }`} style={{ width: 30, height: 30, }} >
                                                                                    <input type="checkbox" className="d-none" checked={ selected } onChange={() =>togglePermission(item.key) } />
                                                                                    {selected && ( <FaCheck size={ 12 } /> )}
                                                                                </div>
                                                                                <div className="flex-grow-1">
                                                                                    <div className={`small fw-medium ${ selected ? "text-white" : " text-dark" }`}> { item.label } </div>
                                                                                    <div className="role-perm-key font-monospace mt-1"> { item.key } </div>
                                                                                    {selected && !!(item.requires || []).length && (
                                                                                        <div className="small text-muted mt-1">Includes: {(item.requires || []).map((k) => labelOf[k] || k).join("; ")}</div>
                                                                                    )}
                                                                                    {selected && (() => {
                                                                                        const missing = (item.recommends || []).filter((k) => !permissions.includes(k));
                                                                                        return missing.length ? (
                                                                                            <div className="small text-warning-emphasis mt-1">
                                                                                                Works best with: {missing.map((k) => labelOf[k] || k).join("; ")}{" "}
                                                                                                <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={(e) => { e.preventDefault(); e.stopPropagation(); addMissing(missing); }}>Add</button>
                                                                                            </div>
                                                                                        ) : null;
                                                                                    })()}
                                                                                    {!selected && neededBy(item.key).length > 0 && (
                                                                                        <div className="small text-warning-emphasis mt-1">Needed by: {neededBy(item.key).join("; ")}. Saved with the role automatically.</div>
                                                                                    )}
                                                                                </div>
                                                                            </label>
                                                                        </div>
                                                                    );
                                                                }
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center py-5 px-3">
                                        <FaShieldAlt size={28} className="text-muted mb-3" />
                                        <h6 className="fw-semibold"> No permissions available </h6>
                                        <p className="text-muted small mb-0"> Permission capabilities could not be loaded. </p>
                                    </div>
                                )}
                            </div>
                        </section>
                        <section>
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="d-flex align-items-center justify-content-center rounded-2 bg-primary bg-opacity-10 text-primary" style={{ width: 34, height: 34, }} >
                                        <FaTasks size={14} />
                                    </div>
                                    <div>
                                        <h6 className="fw-semibold mb-0"> Assignable tasks </h6>
                                        <small className="text-muted"> Delivery / pickup work this role can be assigned. Only these roles appear when assigning a task. </small>
                                    </div>
                                </div>
                                {tasks.length > 0 && (
                                    <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary px-3 py-2">
                                        {tasks.length} selected
                                    </span>
                                )}
                            </div>
                            <div className="bg-white border rounded-4 p-4">
                                {availableTasks.length ? (
                                    <div className="row g-3">
                                        {availableTasks.map( (task) => {
                                                const selected = tasks.includes( task );
                                                return (
                                                    <div key={task} className="col-12 col-sm-6" >
                                                        <label className={`d-flex align-items-center gap-3 border rounded-3 p-3 ${ selected ? "border-primary bg-primary bg-opacity-10" : "" }`} style={{ cursor: "pointer", }} >
                                                            <input type="checkbox" className="d-none" checked={ selected } onChange={() => toggleTask(task) } />
                                                            <div className={`d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${ selected ? "bg-primary text-white" : "bg-light text-muted" }`} style={{ width: 38, height: 38, }} >
                                                                {selected ? (<FaCheck size={ 13 } />) : (<FaTasks size={ 14 } />)}
                                                            </div>

                                                            <div>
                                                                <div className={`fw-semibold text-capitalize ${ selected ? "text-white" : "text-dark" }`} > {task} </div>
                                                                <small className={`${ selected ? "text-white" : "text-dark" }`}> Staff in this role can do this task </small>
                                                            </div>
                                                        </label>
                                                    </div>
                                                );
                                            }
                                        )}

                                    </div>

                                ) : (

                                    <div className="text-center py-4">

                                        <div className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle bg-light text-muted" style={{ width: 50, height: 50, }} >
                                            <FaTasks size={18} />
                                        </div>
                                        <h6 className="fw-semibold mb-1">
                                            No tasks available
                                        </h6>

                                        <p className="text-muted small mb-0">
                                            No task types available.
                                        </p>

                                    </div>

                                )}

                            </div>

                        </section>

                    </div>


                    {/* =================================================
                        FOOTER
                    ================================================== */}

                    <div className="modal-footer border-top bg-white px-4 py-3" style={{ position: "sticky", bottom: 0, zIndex: 5, }} >
                        <div className="w-100 d-flex align-items-center justify-content-between gap-3">
                            <div className="d-none d-md-block text-muted small">
                                <FaClipboardCheck className="me-2" />
                                {permissions.length ? `${permissions.length} permission${permissions.length !== 1 ? "s" : ""} selected` : "Select permissions to continue"}
                            </div>

                            <div className="d-flex gap-2 ms-auto">
                                <button
                                    type="button"
                                    className="btn btn-light border px-4"
                                    data-bs-dismiss="modal"
                                    disabled={saving}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-primary px-4 d-flex align-items-center gap-2"
                                    onClick={onSubmit}
                                    disabled={saving}
                                >

                                    {saving ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm"
                                                role="status"
                                            />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <FaCheck size={12} />

                                            {mode === "edit"
                                                ? "Save changes"
                                                : "Create role"}
                                        </>
                                    )}

                                </button>

                            </div>

                        </div>

                    </div>

                </div>
            </div>
        </div>
    );
};

export default RoleFormModal;
